import { IonContent, IonPage, useIonRouter, useIonViewWillEnter, useIonViewWillLeave } from '@ionic/react';
import { useState, useRef } from 'react';
import { RouteComponentProps } from 'react-router';
import { JaaSMeeting } from '@jitsi/react-sdk';
import { notifyAboutJoinToTheVideoChat } from 'src/api/notifications';
import { getConsultation } from './utils';
import { getJitsiVideoCallToken } from 'src/api/videoCall';
import { Loading } from '@mindly/ui-components';
import { environment } from 'src/enviroments';
import { useTranslation } from 'react-i18next';
import { ConsultationType } from '../types';
import { useLog } from 'src/shared';
import { IJitsiMeetExternalApi } from '@jitsi/react-sdk/lib/types';

type VideoCallPageParams = RouteComponentProps<{
  consultationID: string;
  psychologistID: string;
}>;

const JitsiVideoCall: React.FC<VideoCallPageParams> = ({ match }) => {
  const { consultationID } = match.params;
  const navigation = useIonRouter();
  const {
    i18n: { language },
  } = useTranslation();

  const { logError } = useLog();

  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState<string>('');
  const [consultation, setConsultation] = useState<ConsultationType | null>(null);
  const apiRef = useRef<any>(null);

  const handleApiReady = (api: IJitsiMeetExternalApi) => {
    apiRef.current = api;

    // Define event handlers
    const handleReadyToClose = () => {
      api?.dispose();
      navigation.goBack();
    };

    const handleVideoConferenceLeft = () => {
      api?.dispose();
      navigation.goBack();
    };

    const handleErrorOccurred = (error: any) => {
      logError(error, {
        consultationID,
      });
    };

    const handleCameraError = (error: any) => {
      logError(error, {
        consultationID,
      });
    };

    const handleMicError = (error: any) => {
      logError(error, {
        consultationID,
      });
    };

    // Add event listeners
    api.addListener('readyToClose', handleReadyToClose);
    api.addListener('videoConferenceLeft', handleVideoConferenceLeft);
    api.addListener('errorOccurred', handleErrorOccurred);
    api.addListener('cameraError', handleCameraError);
    api.addListener('micError', handleMicError);

    // Store handlers for cleanup
    apiRef.current.eventHandlers = {
      readyToClose: handleReadyToClose,
      videoConferenceLeft: handleVideoConferenceLeft,
      errorOccurred: handleErrorOccurred,
      cameraError: handleCameraError,
      micError: handleMicError,
    };
  };

  const handleReadyToClose = () => {
    navigation.goBack();
  };

  const jitsiConfig = {
    subject: '',
    startWithAudioMuted: false,
    startWithVideoMuted: false,
    hiddenPremeetingButtons: ['invite'],
    disableProfile: true,
    hideEmailInSettings: true,
    disableInviteFunctions: true,
    readOnlyName: true,
    disablePolls: true,
    hideDisplayName: true,
    prejoinConfig: {
      enabled: true,
      hideDisplayName: true,
    },
    recordings: {
      recordAudioAndVideo: false,
      showPrejoinWarning: false,
      suggestRecording: false,
    },
    giphy: {
      enabled: false,
    },
    disableAddingBackgroundImages: false,
    backgroundAlpha: 0.5,
    enableVirtualBackground: true,
    virtualBackground: {
      enableVirtualBackground: true,
      disableScreensharingVirtualBackground: false,
    },
    participantsPane: {
      enabled: true,
      hideModeratorSettingsTab: true,
      hideMoreActionsButton: true,
      hideMuteAllButton: true,
    },
    resolution: 1080,
    videoQuality: {
      codecPreferenceOrder: ['H264', 'VP8', 'VP9'],
      mobileCodecPreferenceOrder: ['H264', 'VP8', 'VP9'],
    },
    p2p: {
      enabled: true,
      codecPreferenceOrder: ['H264', 'VP8', 'VP9'],
      mobileCodecPreferenceOrder: ['H264', 'VP8', 'VP9'],
    },
    breakoutRooms: {
      hideAddRoomButton: true,
      hideJoinRoomButton: true,
    },
    disableModeratorIndicator: true,
    toolbarButtons: [
      'microphone',
      'camera',
      'desktop',
      'chat',
      'raisehand',
      'participants-pane',
      'tileview',
      'hangup',
      'videoquality',
      'fullscreen',
      'embedmeeting',
      'noisesuppression',
      'select-background',
      'shareaudio',
      'sharedvideo',
      'stats',
      'filmstrip',
      'whiteboard',
      'settings',
    ],
  };

  useIonViewWillEnter(async () => {
    try {
      setIsLoading(true);

      const consultationResult = await getConsultation(consultationID);
      const tokenData = await getJitsiVideoCallToken(consultationID);

      setToken(tokenData.data);
      setConsultation(consultationResult);
      await notifyAboutJoinToTheVideoChat(consultationResult.user_id, consultationResult.psychologist_id);
    } catch (error) {
      logError(error, {
        consultationID,
      });
    } finally {
      setIsLoading(false);
    }
  }, [consultationID]);

  useIonViewWillLeave(() => {
    if (apiRef.current) {
      // Remove all event listeners
      if (apiRef.current.eventHandlers) {
        Object.entries(apiRef.current.eventHandlers).forEach(([eventName, handler]) => {
          apiRef.current.removeListener(eventName, handler);
        });
      }

      apiRef.current.dispose();
      apiRef.current = null;
    }
  });

  return (
    <IonPage>
      <IonContent>
        {isLoading && <Loading isOpen={isLoading} />}
        {!isLoading && token && consultation && (
          <div style={{ width: '100%', height: '100%' }}>
            <JaaSMeeting
              appId={environment.JITSI_APP_ID}
              roomName={consultationID}
              jwt={token}
              configOverwrite={{
                ...jitsiConfig,
                subject: ` `,
              }}
              interfaceConfigOverwrite={{
                DISPLAY_WELCOME_FOOTER: false,
                MAXIMUM_ZOOMING_COEFFICIENT: 1,
              }}
              onApiReady={handleApiReady}
              onReadyToClose={handleReadyToClose}
              getIFrameRef={(iframeRef) => {
                if (iframeRef) {
                  iframeRef.style.height = '100%';
                  iframeRef.style.width = '100%';
                }
              }}
              lang={language}
            />
          </div>
        )}
      </IonContent>
    </IonPage>
  );
};

export default JitsiVideoCall;
